import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, Loader2, Send } from 'lucide-react';
import { toast } from 'sonner';
import { FadeIn, ChapterHeading } from './Reveal';
import { useLang } from '../i18n/LangContext';

const EMAIL_RE = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
const API = process.env.REACT_APP_BACKEND_URL;

const Field = ({ id, label, optional, error, ...props }) => (
  <div>
    <label htmlFor={id} className="block text-xs font-bold uppercase tracking-[0.15em] text-slate-400 mb-2">
      {label} {optional && <span className="text-slate-600 normal-case tracking-normal">{optional}</span>}
    </label>
    <input
      id={id}
      data-testid={`waitlist-input-${id}`}
      aria-invalid={!!error}
      className={`w-full rounded-xl border bg-white/[0.04] px-4 py-3.5 text-sm text-white placeholder:text-slate-600 outline-none transition-colors duration-300 focus:border-krug-tealbright ${
        error ? 'border-red-400/70' : 'border-white/15'
      }`}
      {...props}
    />
    {error && <p data-testid={`waitlist-error-${id}`} className="mt-1.5 text-xs text-red-400">{error}</p>}
  </div>
);

export const Waitlist = () => {
  const { t } = useLang();
  const [form, setForm] = useState({ name: '', email: '', city: '', country: '', newsletter: false, website: '' });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);

  const set = (key) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((er) => ({ ...er, [key]: undefined }));
  };

  const submit = async (e) => {
    e.preventDefault();
    const er = {};
    if (form.name.trim().length < 2) er.name = t.waitlist.errors.name;
    if (!EMAIL_RE.test(form.email.trim())) er.email = t.waitlist.errors.email;
    setErrors(er);
    if (Object.keys(er).length) return;

    setLoading(true);
    try {
      const res = await fetch(`${API}/api/waitlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error('server');
      const data = await res.json();
      setDone(data);
      if (data.duplicate) toast.info(t.waitlist.duplicateMsg);
    } catch {
      toast.error(t.waitlist.failToast);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="waitlist" data-testid="waitlist-section" className="relative bg-krug-navy py-24 md:py-32 overflow-hidden">
      <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[760px] h-[420px] rounded-full bg-krug-teal/12 blur-[150px]" aria-hidden="true" />
      <div className="max-w-3xl mx-auto px-5 md:px-8 relative">
        <ChapterHeading
          number="10"
          eyebrow={t.waitlist.eyebrow}
          title={t.waitlist.title}
          copy={t.waitlist.copy}
          align="center"
        />

        <FadeIn delay={0.15} className="mt-12">
          <div className="rounded-[2rem] glass-dark p-7 md:p-10">
            <AnimatePresence mode="wait">
              {done ? (
                <motion.div
                  key="success"
                  data-testid="waitlist-success"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5 }}
                  className="py-10 text-center"
                >
                  <span className="mx-auto inline-flex items-center justify-center w-16 h-16 rounded-full bg-krug-teal/15 text-krug-tealbright">
                    <CheckCircle2 size={32} />
                  </span>
                  <h3 className="mt-6 font-heading text-2xl md:text-3xl font-extrabold text-white">
                    {t.waitlist.successTitle}
                  </h3>
                  <p className="mt-3 text-slate-400">{done.duplicate ? t.waitlist.duplicateMsg : t.waitlist.successMsg}</p>
                </motion.div>
              ) : (
                <motion.form
                  key="form"
                  data-testid="waitlist-form"
                  onSubmit={submit}
                  noValidate
                  exit={{ opacity: 0, y: -12 }}
                  className="space-y-5"
                >
                  <input
                    type="text"
                    name="website"
                    value={form.website}
                    onChange={set('website')}
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    className="hidden"
                  />
                  <div className="grid sm:grid-cols-2 gap-5">
                    <Field id="name" label={t.waitlist.labels.name} placeholder={t.waitlist.placeholders.name} value={form.name} onChange={set('name')} error={errors.name} autoComplete="name" />
                    <Field id="email" label={t.waitlist.labels.email} type="email" placeholder={t.waitlist.placeholders.email} value={form.email} onChange={set('email')} error={errors.email} autoComplete="email" />
                    <Field id="city" label={t.waitlist.labels.city} optional={t.waitlist.labels.optional} placeholder={t.waitlist.placeholders.city} value={form.city} onChange={set('city')} />
                    <Field id="country" label={t.waitlist.labels.country} optional={t.waitlist.labels.optional} placeholder={t.waitlist.placeholders.country} value={form.country} onChange={set('country')} />
                  </div>

                  <label data-testid="waitlist-newsletter-label" className="flex items-start gap-3 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      data-testid="waitlist-checkbox-newsletter"
                      checked={form.newsletter}
                      onChange={set('newsletter')}
                      className="mt-0.5 w-4.5 h-4.5 w-[18px] h-[18px] rounded accent-krug-gold"
                    />
                    <span className="text-sm text-slate-300">{t.waitlist.newsletter}</span>
                  </label>

                  <button
                    type="submit"
                    data-testid="waitlist-submit"
                    disabled={loading}
                    className="group inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-full bg-krug-gold px-8 py-4 font-heading font-bold text-krug-navy hover:bg-krug-goldbright disabled:opacity-60 transition-colors duration-300"
                  >
                    {loading ? <Loader2 size={18} className="animate-spin" /> : <Send size={17} className="transition-transform duration-300 group-hover:translate-x-0.5" />}
                    {t.waitlist.submit}
                  </button>

                  <p data-testid="waitlist-gdpr-note" className="text-xs leading-relaxed text-slate-500">
                    {t.waitlist.gdprBefore}
                    <Link to="/privacy" data-testid="waitlist-privacy-link" className="text-krug-tealbright hover:underline">
                      {t.waitlist.gdprLink}
                    </Link>
                    {t.waitlist.gdprAfter}
                  </p>
                </motion.form>
              )}
            </AnimatePresence>
          </div>
        </FadeIn>
      </div>
    </section>
  );
};
